import React, { PropsWithChildren, createContext, useContext, useEffect, useRef } from 'react';
import useApi from '@/hooks/useApi/useApi';
import { useFirebaseMessaging } from '@/hooks/useFirebaseMessaging';
import { useStore } from '@/stores/store';
import { User } from '@/typings/user';
import useRollbar from '@/hooks/useRollbar';
import { initMessaging } from '@/utils/firebase';
import { useSerwist } from '@serwist/turbopack/react';

type NotificationBannerType = {
  requestPermission: () => Promise<void>;
  token: string | null;
  permissionStatus: NotificationPermission;
};

const NotificationBannerContext = createContext({} as NotificationBannerType);

const SW_RESET_FLAG_KEY = 'notification_sw_reset_at';
const SW_RESET_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
const TOKEN_GENERATION_TIMEOUT_MS = 15_000;

const NotificationBannerProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const verifiedToken = useRef(false);
  const tokenCheckTimeout = useRef<ReturnType<typeof setTimeout>>(null);

  const updateData = useStore((store) => store.updateData);
  const user = useStore((store) => store.user);

  const [requestPermission, { permissionStatus, token }] = useFirebaseMessaging();
  const { post } = useApi('notificationToken');
  const rollbar = useRollbar();
  const { serwist } = useSerwist();

  useEffect(() => {
    if (!serwist) return;

    const showSkipWaitingPrompt = async () => {
      serwist.addEventListener('controlling', () => {
        window.location.reload();
      });

      serwist.messageSkipWaiting();
    };

    serwist.addEventListener('waiting', showSkipWaitingPrompt);
  }, [serwist]);

  useEffect(() => {
    if (!user) return; // Should only post to the backend when the user is available
    if (verifiedToken.current) return; // Token already posted

    const activeTokens = (user.notificationTokensWeb || []).filter((tk) => !tk.deactivatedAt);
    const tokenMissingFromList = token && !activeTokens.some((tk) => tk.token === token);
    const noTokenWithPermission = !token && permissionStatus === 'granted';

    // Token is valid and registered
    if (!tokenMissingFromList && !noTokenWithPermission) {
      if (tokenCheckTimeout.current) {
        clearTimeout(tokenCheckTimeout.current);
        tokenCheckTimeout.current = null;
      }
      return;
    }

    const handleTokenPost = (token: string) => {
      // rollbar.debug('Adding new token to a user', { user, token, permissionStatus });
      post(
        { token, userAgent: navigator.userAgent },
        {
          error: {
            message: 'Failed to register user notification token',
            showNotification: false,
            shouldSkipLogging: () => true,
          },
          onSuccess: (updatedUser: User) => {
            updateData<User>('user', (user) => ({ ...user, ...updatedUser }));
            verifiedToken.current = true;
          },
          onError: (error) => {
            const baseMessage = 'Failed to register user notification token';
            const rollbarMessage = error?.message
              ? `${baseMessage}: ${error.message}`
              : baseMessage;
            rollbar.error(rollbarMessage, {
              token,
              userAgent: navigator.userAgent,
              errorMsg: error?.message,
            });
          },
        },
      );
    };

    if (token) {
      console.log(`[notification] Token generated`);
      verifiedToken.current = true;
      handleTokenPost(token);
      if (tokenCheckTimeout.current) {
        clearTimeout(tokenCheckTimeout.current);
        tokenCheckTimeout.current = null;
      }
      return;
    }

    // No token with permission granted — activate waiting SW if available, then regenerate token.
    // Handles cases where the user already allowed notifications but has no token:
    // e.g. logged out and back in with a different account, or the service worker was outdated.
    verifiedToken.current = true;

    // If we cannot generate a firebase messaging token in TOKEN_GENERATION_TIMEOUT_MS
    // something went wrong in the service worker. To avoid having to ask the user to
    // manually cleanup the browser data, we force the unregister and reload the page
    // so that we get the new service worker.
    const resetServiceWorkerAndReload = async (reason: string) => {
      const lastResetStr = localStorage.getItem(SW_RESET_FLAG_KEY);
      const now = Date.now();

      // Check the last time we reset to avoid entering in a reload loop
      if (lastResetStr) {
        const lastReset = parseInt(lastResetStr, 10);
        if (!isNaN(lastReset) && now - lastReset < SW_RESET_COOLDOWN_MS) {
          rollbar.debug('[notification] Skipping SW reset — already reset recently', {
            lastReset,
            reason,
          });
          return;
        }
      }

      rollbar.debug('[notification] Unregistering service worker and reloading', { reason });
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) await registration.unregister();
      } catch (unregisterError) {
        rollbar.error(
          '[notification] Failed to unregister service worker',
          unregisterError as Error,
        );
      }

      localStorage.setItem(SW_RESET_FLAG_KEY, String(now));
      window.location.reload();
    };

    const handleTokenRefresh = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        const hasUpdate = !!registration?.waiting;
        console.log('[notification] No token found, forcing token generation', { hasUpdate });
        rollbar.debug('[notification] No token found, forcing token generation', { hasUpdate });

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error('Token generation timed out')),
            TOKEN_GENERATION_TIMEOUT_MS,
          ),
        );

        const newToken = await Promise.race([initMessaging(true), timeoutPromise]);
        const tokenMissingFromList = newToken && !activeTokens.some((tk) => tk.token === newToken);
        if (tokenMissingFromList) handleTokenPost(newToken);
      } catch (error) {
        rollbar.error(
          'Failed to refresh Firebase cloud messaging token - notificationBanner',
          error as Error,
        );
        await resetServiceWorkerAndReload((error as Error).message);
      }
    };

    // We may have to wait a few seconds for the service worker to start and
    // generate/retrieve the token. Set a timeout before logging the problem
    tokenCheckTimeout.current = setTimeout(handleTokenRefresh, 3000);
  }, [user, permissionStatus, token]);

  return (
    <NotificationBannerContext.Provider value={{ permissionStatus, token, requestPermission }}>
      {children}
    </NotificationBannerContext.Provider>
  );
};

export const useNotificationBanner = () => useContext(NotificationBannerContext);

export default NotificationBannerProvider;
